/**
 * @fileoverview The hand-over of a photo that came late from Google Photos
 * (B40), from the app's frame to the screen that was waiting for it.
 *
 * A seed-packet scan and Today's-picture dialog each need their own screen's
 * providers, which the app's frame (GooglePhotosResume) does not have. When
 * a pick for one of them returns after its screen closed, the frame holds
 * the photo here, in memory only (nothing of the gardener's goes in browser
 * storage, rule 6), opens the page, and the screen, as soon as it is on
 * screen, takes the photo and goes on as if it had just been chosen. A photo
 * not taken within LATE_HANDOFF_HOLD_MS is dropped. One photo per purpose.
 *
 * Pure but for its held values and a clock passed in, so it runs under plain
 * node. No imports.
 *
 * Where: GooglePhotosResume holds; TodayPictureSetting takes (the packet purpose lost its taker with usePacketScan, B80).
 * API: none.
 *
 * @module lateHandoff
 */

/** What a held photo is waiting for. */
export type HandoffPurpose = 'packet' | 'today';

/**
 * How long a held photo waits for its screen, in milliseconds. The screen is
 * on the page that opens straight after; ten minutes covers a slow page and
 * no more, since nothing of the gardener's is kept past the tab.
 * @kept K1
 */
export const LATE_HANDOFF_HOLD_MS = 600_000;

/** A photo waiting for a screen. */
interface HeldPhoto {
  /** The photo. */
  file: File;
  /** The bed the scan was started for, or null when it goes by the label (or the purpose has none). */
  locationId: number | null;
  /** When it was held, in milliseconds since the epoch. */
  at: number;
}

/** The photo held for each purpose. */
const held = new Map<HandoffPurpose, HeldPhoto>();
/** Who to tell when a photo is held. */
const listeners = new Set<() => void>();

/**
 * Holds a photo for the next screen of this purpose that fits, and tells the
 * screens on view. Replaces any photo already held for the purpose.
 *
 * @param purpose - What the photo is for.
 * @param file - The photo.
 * @param locationId - The bed the scan was for, or null.
 * @param now - The time, in milliseconds since the epoch.
 */
export function holdLate(purpose: HandoffPurpose, file: File, locationId: number | null, now: number): void {
  held.set(purpose, { file, locationId, at: now });
  listeners.forEach((listener) => listener());
}

/**
 * Takes the held photo if it is for this screen and has not run out. A screen
 * with no bed of its own takes any; a bed's takes its own, or one held with no
 * bed.
 *
 * @param purpose - What the screen is for.
 * @param locationId - The bed this screen is for, or null.
 * @param now - The time, in milliseconds since the epoch.
 * @param holdMs - How long a photo is held.
 * @param noBedOnly - True for a screen that must never take a photo held for a specific bed (Today's start card): it takes only one held with no bed.
 * @returns The photo, which is then no longer held; or null.
 */
export function takeLate(purpose: HandoffPurpose, locationId: number | null, now: number, holdMs: number, noBedOnly = false): File | null {
  const photo = held.get(purpose);
  if (!photo) return null;
  if (now - photo.at > holdMs) {
    held.delete(purpose);
    return null;
  }
  if (noBedOnly && photo.locationId !== null) return null;
  const fits = locationId === null || photo.locationId === null || photo.locationId === locationId;
  if (!fits) return null;
  held.delete(purpose);
  return photo.file;
}

/**
 * Whether a photo is waiting for a screen of this purpose.
 *
 * @param purpose - What the photo is for.
 * @returns True while one is held.
 */
export function hasLate(purpose: HandoffPurpose): boolean {
  return held.has(purpose);
}

/**
 * Asks to be told when a photo is held.
 *
 * @param listener - Called on each hold.
 * @returns Stops the telling.
 */
export function onLate(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Forgets every held photo and every listener. For tests. */
export function resetLate(): void {
  held.clear();
  listeners.clear();
}
